'use client';

import { Box, Divider, Pagination, Typography } from '@wishket/design-system';
import { forwardRef, useEffect, useState, Fragment } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import ArticleComment from '../ArticleComment';
import ArticleCommentForm from '../ArticleCommentForm';

import { useCommentList } from '@/entities/article';
import { useDeviceCheck } from '@/shared/hooks';
import { PATH } from '@/shared/constants';

interface ArticleCommentsSectionProps {
  id: string;
  isVisible: boolean;
}

const LAST_INDEX_OF_PAGE = 9;
const TOP_BANNER_GAP = 64;

const ArticleCommentsSection = forwardRef<
  HTMLDivElement,
  ArticleCommentsSectionProps
>(({ id, isVisible }, ref) => {
  const { isDesktop } = useDeviceCheck();
  const router = useRouter();
  const params = useSearchParams();
  const pageParam = params.get('page');

  const [page, setPage] = useState(1);

  const [targetComment, setTargetComment] = useState<number | undefined>(
    undefined
  );
  const [newCommentId, setNewCommentId] = useState<number | undefined>(
    undefined
  );

  const { data: commentList } = useCommentList({ id, page });
  const hasMoreThanOneComment = commentList && commentList.count > 0;

  const openTargetComment = (id: number) => setTargetComment(id);

  const closeTargetComment = () => setTargetComment(undefined);

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
    const isCommentSectionRefAvailable =
      ref && typeof ref === 'object' && 'current' in ref && ref.current;

    if (pageParam)
      router.replace(`${PATH.DETAIL}/${id}`, {
        scroll: false,
      });

    if (isCommentSectionRefAvailable) {
      const scrollPosition = (ref.current?.offsetTop || 0) - TOP_BANNER_GAP;
      window.scrollTo({ top: scrollPosition });
    }
  };

  useEffect(() => {
    const currentPageParam = Number(pageParam);
    const validPageNumber =
      pageParam === null || isNaN(currentPageParam) ? 1 : currentPageParam;

    if (pageParam) onPageChange(validPageNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParam]);

  return (
    <section className="flex h-fit w-full flex-col gap-12">
      <Box ref={ref} className="flex w-full flex-col gap-12">
        {commentList && isVisible && (
          <>
            <Box className="flex w-full flex-col gap-8">
              <Typography
                variant="subTitle1"
                className="font-medium text-w-gray-900"
              >
                댓글 <span className="text-primary">{commentList.count}</span>
              </Typography>
              {commentList.comments.map((comment, index) => {
                const isLastOfPage = index === LAST_INDEX_OF_PAGE;
                const targetStatus = {
                  isSelected: targetComment === comment.id,
                  open: openTargetComment,
                  close: closeTargetComment,
                };
                const pageStatus = {
                  current: page,
                  change: onPageChange,
                };

                return (
                  <Fragment key={comment.id}>
                    <ArticleComment
                      newsId={id}
                      newCommentId={newCommentId}
                      resetNewCommentId={() => setNewCommentId(undefined)}
                      pageStatus={pageStatus}
                      target={targetStatus}
                      comment={comment}
                      isLastOfPage={isLastOfPage}
                    />
                    <Divider />
                  </Fragment>
                );
              })}
            </Box>
            {hasMoreThanOneComment && (
              <Pagination
                pageCount={isDesktop ? 10 : 5}
                page={page}
                maxPage={commentList.maxPage}
                onChange={onPageChange}
              />
            )}
          </>
        )}
      </Box>
      {isVisible && (
        <ArticleCommentForm
          newsId={id}
          totalCommentCount={commentList?.count}
          onPageChange={onPageChange}
          setNewCommentId={setNewCommentId}
        />
      )}
    </section>
  );
});

ArticleCommentsSection.displayName = 'ArticleCommentsSection';

export default ArticleCommentsSection;
